const AUTH_STORAGE_KEYS = ['roles', 'employeeNumber', 'userPractice', 'userCategory', 'employeeId', 'userId'] as const;
const LEGACY_AUTH_STORAGE_KEYS = ['refreshToken', 'token', 'accessToken', 'userEmail', 'username'] as const;
export type AuthStorageKey = (typeof AUTH_STORAGE_KEYS)[number];

/**
 * Reads and validates 'roles' from localStorage.
 * Returns [] and removes the key if the value is malformed or not an array of strings.
 */
export function safeReadRoles(): string[] {
  let raw: string | null;
  try {
    raw = localStorage.getItem('roles');
  } catch (err) {
    console.error('[auth] Storage read failed for "roles"', err);
    return [];
  }

  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.every((r) => typeof r === 'string')) {
      throw new Error('invalid format');
    }
    return parsed as string[];
  } catch {
    console.warn('[auth] Malformed "roles" in storage — clearing and recovering.');
    try {
      localStorage.removeItem('roles');
    } catch { /* storage may be unavailable */ }
    return [];
  }
}

/**
 * Reads a string key from localStorage. Returns null on any error or if the
 * stored value is absent or empty.
 */
export function safeReadStorageString(key: AuthStorageKey): string | null {
  try {
    const val = localStorage.getItem(key);
    return typeof val === 'string' && val.trim().length > 0 ? val : null;
  } catch (err) {
    console.error(`[auth] Storage read failed for "${key}"`, err);
    return null;
  }
}

/**
 * Removes all auth-related keys from localStorage.
 * Legacy keys (userEmail, username, token variants) are also cleared so that
 * any values written by a previous version of the app are purged on logout.
 */
export function clearAuthStorage(): void {
  [...AUTH_STORAGE_KEYS, ...LEGACY_AUTH_STORAGE_KEYS].forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch { /* ignore */ }

    try {
      sessionStorage.removeItem(key);
    } catch { /* ignore */ }
  });
}

/**
 * Validates auth-related localStorage keys at startup.
 * Malformed values are cleared so they cannot corrupt in-memory state.
 */
export function sanitizeAuthStorage(): void {
  safeReadRoles();
}
